import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { createGoogleMap, createGoogleMapLocationMarker } from '../../services/googleMapFactory';

const GoogleLocationMap = ({ center, zoom = 17, options, onLocationClick, onError }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const clickListenerRef = useRef(null);
  const onLocationClickRef = useRef(onLocationClick);
  const onErrorRef = useRef(onError);
  const initialCenterRef = useRef(center);
  const initialOptionsRef = useRef(options);
  const initialZoomRef = useRef(zoom);
  const initializedRef = useRef(false);
  const centerLat = center.lat;
  const centerLng = center.lng;

  useEffect(() => {
    onLocationClickRef.current = onLocationClick;
  }, [onLocationClick]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    let isMounted = true;
    const mapElement = containerRef.current;

    const initializeMap = async () => {
      if (initializedRef.current || !mapElement || !initialCenterRef.current) return;

      initializedRef.current = true;

      try {
        const map = await createGoogleMap(mapElement, {
          center: initialCenterRef.current,
          zoom: initialZoomRef.current,
          fullscreenControl: false,
          ...initialOptionsRef.current,
        });

        if (!isMounted) {
          mapElement.replaceChildren();
          return;
        }

        mapRef.current = map;
        const marker = await createGoogleMapLocationMarker(map, initialCenterRef.current);

        if (!isMounted) {
          marker.setMap(null);
          return;
        }

        markerRef.current = marker;

        clickListenerRef.current = map.addListener('click', (event) => {
          if (!event.latLng) return;

          onLocationClickRef.current?.({
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
          });
        });
      } catch (error) {
        initializedRef.current = false;
        console.error('Failed to initialize Google Map.', error);
        onErrorRef.current?.(error);
      }
    };

    initializeMap();

    return () => {
      isMounted = false;

      if (clickListenerRef.current) {
        clickListenerRef.current.remove();
        clickListenerRef.current = null;
      }

      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }

      mapElement?.replaceChildren();
      mapRef.current = null;
      initializedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;

    const nextCenter = { lat: centerLat, lng: centerLng };
    mapRef.current.setCenter(nextCenter);
    markerRef.current?.setPosition(nextCenter);
  }, [centerLat, centerLng]);

  return <div className='google-location-map' ref={containerRef} />;
};

GoogleLocationMap.propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }).isRequired,
  zoom: PropTypes.number,
  options: PropTypes.object,
  onLocationClick: PropTypes.func,
  onError: PropTypes.func,
};

export default GoogleLocationMap;
